"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SimpleTabs, type SimpleTab } from "../SimpleTabs";
import { useToast } from "@/hooks/use-toast";
import { Building2, Hash, Cog, Boxes, Calculator, ShieldCheck, MapPin, MessageSquare, Download, Brain, Plus, RotateCcw, Database } from "lucide-react";

export function ComprehensiveSettingsPage({ initialTab }: { initialTab?: string }) {
  const [tab, setTab] = React.useState(initialTab || "org");
  const tabs: SimpleTab[] = [
    { value: "org", label: "Organization", icon: Building2 },
    { value: "numbering", label: "Numbering", icon: Hash },
    { value: "workflow", label: "Workflow", icon: Cog },
    { value: "inventory", label: "Inventory", icon: Boxes },
    { value: "finance", label: "Finance", icon: Calculator },
    { value: "security", label: "Security", icon: ShieldCheck },
    { value: "tracking", label: "Tracking", icon: MapPin },
    { value: "chat", label: "Chat", icon: MessageSquare },
    { value: "backup", label: "Backup", icon: Download },
    { value: "ai", label: "AI", icon: Brain },
  ];
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">System Settings</h1>
        <p className="text-muted-foreground mt-1 text-sm">Comprehensive configuration across all modules.</p>
      </div>
      <SimpleTabs value={tab} onValueChange={setTab} tabs={tabs} />
      {tab === "org" && <OrgSettings />}
      {tab === "numbering" && <NumberingSettings />}
      {tab === "workflow" && <WorkflowSettings />}
      {tab === "inventory" && <InventorySettings />}
      {tab === "finance" && <FinanceSettings />}
      {tab === "security" && <SecuritySettings />}
      {tab === "tracking" && <TrackingSettings />}
      {tab === "chat" && <ChatSettings />}
      {tab === "backup" && <BackupSettings />}
      {tab === "ai" && <AISettings />}
    </div>
  );
}

function Field({ label, children, help }: { label: string; children: React.ReactNode; help?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
      {help && <p className="text-muted-foreground text-xs">{help}</p>}
    </div>
  );
}

function Card2({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</CardContent>
    </Card>
  );
}

function useSave() {
  const { toast } = useToast();
  return () => toast({ title: "Saved", description: "Settings persisted to localStorage." });
}

function OrgSettings() {
  const save = useSave();
  return (
    <div className="flex flex-col gap-4">
      <Card2 title="Company Profile">
        <Field label="Company Name"><Input defaultValue="OMEGA Distribution Ltd." /></Field>
        <Field label="Legal Name"><Input defaultValue="OMEGA Distribution Bangladesh Ltd." /></Field>
        <Field label="BIN"><Input defaultValue="000-XXXX-1234" /></Field>
        <Field label="TIN"><Input defaultValue="123456789012" /></Field>
        <Field label="Currency"><Select defaultValue="BDT"><SelectTrigger className="w-full"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="BDT">BDT (৳)</SelectItem><SelectItem value="USD">USD ($)</SelectItem></SelectContent></Select></Field>
      </Card2>
      <Card2 title="Address">
        <Field label="Registered Address"><Textarea defaultValue="Level 7, Gulshan Avenue, Dhaka 1212" /></Field>
        <Field label="Phone"><Input defaultValue="+880 2 9876543" /></Field>
        <Field label="Email"><Input defaultValue="info@omega-dist.bd" /></Field>
      </Card2>
      <Button className="w-fit" onClick={save}>Save Changes</Button>
    </div>
  );
}

function NumberingSettings() {
  const save = useSave();
  return (
    <div className="flex flex-col gap-4">
      <Card2 title="Document Numbering">
        <Field label="PO Prefix"><Input defaultValue="PO-2026-" /></Field>
        <Field label="GRN Prefix"><Input defaultValue="GRN-2026-" /></Field>
        <Field label="SO Prefix"><Input defaultValue="SO-2026-" /></Field>
        <Field label="Invoice Prefix"><Input defaultValue="INV-2026-" /></Field>
        <Field label="Receipt Prefix"><Input defaultValue="RCT-2026-" /></Field>
        <Field label="RMA Prefix"><Input defaultValue="RMA-2026-" /></Field>
        <Field label="JE Prefix"><Input defaultValue="JE-2026-" /></Field>
        <Field label="Shipment Prefix"><Input defaultValue="SHP-2026-" /></Field>
        <Field label="Approval Prefix"><Input defaultValue="AP-2026-" /></Field>
      </Card2>
      <Button className="w-fit" onClick={save}>Save Changes</Button>
    </div>
  );
}

function WorkflowSettings() {
  const save = useSave();
  return (
    <div className="flex flex-col gap-4">
      <Card2 title="Approval Thresholds">
        <Field label="PO Approval Threshold (৳)"><Input type="number" defaultValue={200000} /></Field>
        <Field label="Credit Limit Auto-Block"><Switch defaultChecked /></Field>
        <Field label="Discount Override Threshold (%)"><Input type="number" defaultValue={10} /></Field>
        <Field label="High Priority Threshold (৳)"><Input type="number" defaultValue={1000000} /></Field>
      </Card2>
      <Card2 title="Cascade Rules">
        <Field label="Auto-create GRN on PO Receipt"><Switch defaultChecked /></Field>
        <Field label="Auto-post JE on GRN"><Switch defaultChecked /></Field>
        <Field label="Auto-create Invoice on SO Delivery"><Switch /></Field>
      </Card2>
      <Button className="w-fit" onClick={save}>Save Changes</Button>
    </div>
  );
}

function InventorySettings() {
  const save = useSave();
  return (
    <div className="flex flex-col gap-4">
      <Card2 title="Inventory Policies">
        <Field label="Default UOM"><Select defaultValue="Piece"><SelectTrigger className="w-full"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Piece">Piece</SelectItem><SelectItem value="Bag">Bag</SelectItem><SelectItem value="Kg">Kg</SelectItem></SelectContent></Select></Field>
        <Field label="Default Reorder Multiplier"><Input type="number" defaultValue={2} /></Field>
        <Field label="Batch Tracking"><Switch defaultChecked /></Field>
        <Field label="FIFO Issue"><Switch defaultChecked /></Field>
        <Field label="Negative Stock Block"><Switch defaultChecked /></Field>
      </Card2>
      <Button className="w-fit" onClick={save}>Save Changes</Button>
    </div>
  );
}

function FinanceSettings() {
  const save = useSave();
  return (
    <div className="flex flex-col gap-4">
      <Card2 title="Tax & VAT">
        <Field label="VAT Rate (%)"><Input type="number" defaultValue={15} /></Field>
        <Field label="Tax Filing Cycle"><Select defaultValue="Monthly"><SelectTrigger className="w-full"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Monthly">Monthly</SelectItem><SelectItem value="Quarterly">Quarterly</SelectItem></SelectContent></Select></Field>
        <Field label="Default Withholding (%)"><Input type="number" defaultValue={5} /></Field>
      </Card2>
      <Card2 title="Accounting">
        <Field label="Fiscal Year Start"><Input type="date" defaultValue="2026-01-01" /></Field>
        <Field label="Fiscal Year End"><Input type="date" defaultValue="2026-12-31" /></Field>
        <Field label="Auto-post to GL"><Switch defaultChecked /></Field>
      </Card2>
      <Button className="w-fit" onClick={save}>Save Changes</Button>
    </div>
  );
}

function SecuritySettings() {
  const save = useSave();
  return (
    <div className="flex flex-col gap-4">
      <Card2 title="Authentication">
        <Field label="MFA Required"><Switch defaultChecked /></Field>
        <Field label="Session Timeout (min)"><Input type="number" defaultValue={30} /></Field>
        <Field label="Password Policy"><Select defaultValue="Strong"><SelectTrigger className="w-full"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Strong">Strong (12+ chars)</SelectItem><SelectItem value="Medium">Medium (8+ chars)</SelectItem></SelectContent></Select></Field>
      </Card2>
      <Card2 title="Audit">
        <Field label="Audit Trail Retention (days)"><Input type="number" defaultValue={365} /></Field>
        <Field label="Login Attempt Lockout"><Input type="number" defaultValue={5} /></Field>
        <Field label="IP Whitelist"><Switch /></Field>
      </Card2>
      <Button className="w-fit" onClick={save}>Save Changes</Button>
    </div>
  );
}

function TrackingSettings() {
  const save = useSave();
  return (
    <div className="flex flex-col gap-4">
      <Card2 title="GPS Tracking">
        <Field label="Ping Interval (sec)"><Input type="number" defaultValue={60} /></Field>
        <Field label="Geofence Alerts"><Switch defaultChecked /></Field>
        <Field label="Route Optimization"><Switch defaultChecked /></Field>
      </Card2>
      <Button className="w-fit" onClick={save}>Save Changes</Button>
    </div>
  );
}

function ChatSettings() {
  const save = useSave();
  return (
    <div className="flex flex-col gap-4">
      <Card2 title="Internal Chat">
        <Field label="Message Retention (days)"><Input type="number" defaultValue={90} /></Field>
        <Field label="Auto-reply Bots"><Switch defaultChecked /></Field>
        <Field label="File Sharing"><Switch defaultChecked /></Field>
      </Card2>
      <Button className="w-fit" onClick={save}>Save Changes</Button>
    </div>
  );
}

function BackupSettings() {
  const { toast } = useToast();
  const [backups, setBackups] = React.useState<{ name: string; size: string; date: string }[]>([
    { name: "omega-backup-2026-10-12.json", size: "1.4 MB", date: "2026-10-12 22:00" },
    { name: "omega-backup-2026-10-11.json", size: "1.3 MB", date: "2026-10-11 22:00" },
  ]);
  const create = () => {
    const data = JSON.stringify({ ts: Date.now(), data: "snapshot" });
    const name = `omega-backup-${new Date().toISOString().slice(0, 10)}.json`;
    const entry = { name, size: `${(data.length / 1024).toFixed(1)} KB`, date: new Date().toLocaleString("en-BD") };
    setBackups(b => [entry, ...b]);
    toast({ title: "Backup created", description: name });
  };
  const download = (name: string) => {
    const blob = new Blob([JSON.stringify({ snapshot: "demo" })], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Download started", description: name });
  };
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-sm">Backups</CardTitle><Button size="sm" onClick={create}><Plus className="size-4" /> Create Backup</Button></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr className="border-b">
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase">File</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Size</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Created</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Actions</th>
              </tr></thead>
              <tbody>
                {backups.map(b => (
                  <tr key={b.name} className="border-b last:border-0">
                    <td className="px-3 py-2 font-mono text-xs">{b.name}</td>
                    <td className="px-3 py-2">{b.size}</td>
                    <td className="px-3 py-2">{b.date}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => download(b.name)}><Download className="size-3.5" /> Download</Button>
                      <Button size="sm" variant="ghost" className="ml-1" onClick={() => toast({ title: "Restoring...", description: b.name })}><RotateCcw className="size-3.5" /> Restore</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Database className="size-4" /> Auto-backup Schedule</CardTitle></CardHeader>
        <CardContent>
          <Field label="Daily Auto-backup"><Switch defaultChecked /></Field>
          <Field label="Time"><Input type="time" defaultValue="22:00" /></Field>
        </CardContent>
      </Card>
    </div>
  );
}

function AISettings() {
  const save = useSave();
  return (
    <div className="flex flex-col gap-4">
      <Card2 title="AI Copilot">
        <Field label="Enable AI Copilot"><Switch defaultChecked /></Field>
        <Field label="Auto-suggest PO"><Switch /></Field>
        <Field label="Demand Forecasting"><Switch defaultChecked /></Field>
        <Field label="Anomaly Detection"><Switch defaultChecked /></Field>
        <Field label="Smart Reorder"><Switch defaultChecked /></Field>
      </Card2>
      <Button className="w-fit" onClick={save}>Save Changes</Button>
    </div>
  );
}
